import { useEffect, useMemo } from 'react';
import { useError } from 'react-use';

import { useRestPostQuery } from 'utils/hooks/api/useRestPostQuery';
import { InsightQueryRequest, ProfileQuery, DEFAULT_QUERY } from '../../domain';
import { retryOnPartialContent } from '../utils';
import { useInsightsResponseHandler } from './useInsightsResponseHandler';

const PROFILE_INSIGHTS_API = '/lists/insights/demographic';

const defaultProfileQuery: ProfileQuery = {
  emailDomains: [],
  brandAffinityLevels: [],
  devices: [],
  locations: [],
};

export const useProfileInsightsQuery = ({
  searchQuery,
  emailListId,
  endDate,
  startDate,
}: InsightQueryRequest = DEFAULT_QUERY) => {
  const params = useMemo(
    () => ({
      searchQuery,
      emailListId,
      endDate,
      startDate,
    }),
    [searchQuery, emailListId, endDate, startDate],
  );
  const {
    handleResponse,
    data = defaultProfileQuery,
    isRetrying,
  } = useInsightsResponseHandler<ProfileQuery>();

  const { refetch, isLoading, ...results } = useRestPostQuery<ProfileQuery, InsightQueryRequest>(
    PROFILE_INSIGHTS_API,
    params,
    {
      enabled: false,
      retry: retryOnPartialContent,
      onError: useError(),
    },
    undefined,
    handleResponse,
  );

  useEffect(() => {
    if (params?.emailListId) {
      refetch();
    }
  }, [params]);

  return {
    ...results,
    data,
    isLoading: isLoading && !isRetrying,
    refetch,
  };
};
